import * as React from 'react';
import { connect } from 'react-redux';
import { first, isNil, last } from 'lodash';

import MessagesByDate from './MessagesByDate';
import Spinner from 'components/spinner/Spinner';

import { POLLING_INTERVAL } from 'utils/messenger';

import type {
  Campaign,
  MessengerGroupedMessages,
  MessengerMessage,
  Subcampaign
} from 'types';

type StateProps = {
  isLoadingMore: boolean,
  isLoading: boolean,
  groupedMessages: {
    [key: string]: MessengerGroupedMessages
  }
};

type OwnProps = {
  campaign: Campaign,
  subcampaign: Subcampaign,
  subcampaignId: number,
  isBrand: boolean,
  onLoadPreviousPage: () => void,
  onLoadNewMessages: () => void
};

type Props = StateProps & OwnProps;

type LastestSystemMessageForInfluencerAction = {
  message: MessengerMessage,
  groupKey: string
};

class MessagesList extends React.PureComponent<Props> {
  messagesList: HTMLElement | undefined | null;

  intervalId: any;

  componentDidMount(): void {
    if (this.messagesList) {
      this.messagesList.addEventListener('scroll', this.handleScroll);
    }

    if (window.app.env.FEATURE_NEW_MESSAGES_POLLING) {
      this.initPolling();
    }

    this.scrollToBottom();
  }

  componentDidUpdate(prevProps: Props): void {
    const { groupedMessages, isLoading } = this.props;

    if (
      isLoading ||
      (prevProps.isLoading && !isLoading) || // if we have it on memory but we load it again we need to scroll to bottom
      isNil(prevProps.groupedMessages) ||
      isNil(groupedMessages)
    ) {
      this.scrollToBottom();
      return;
    }

    const prevGroupedMessagesValues: Array<MessengerGroupedMessages> = Object.values(prevProps.groupedMessages);
    const groupedMessagesValues: Array<MessengerGroupedMessages> = Object.values(groupedMessages);

    const firstGroup = first(groupedMessagesValues);
    const lastGroup = last(groupedMessagesValues);

    const prevFirstGroup = first(prevGroupedMessagesValues);
    const prevLastGroup = last(prevGroupedMessagesValues);

    const firstMessage = first(firstGroup?.messages);
    const lastMessage = last(lastGroup?.messages);

    const prevFirstMessage = first(prevFirstGroup?.messages);
    const prevLastMessage = last(prevLastGroup?.messages);

    if (!isLoading) {
      const shouldScrollBottom = Boolean(lastMessage) && Boolean(prevLastMessage) && (lastMessage?.externalId !== prevLastMessage?.externalId);
      const shouldKeepPosition = Boolean(firstMessage) && Boolean(prevFirstMessage) && (firstMessage?.externalId !== prevFirstMessage?.externalId);

      if (shouldScrollBottom) {
        this.scrollToBottom();
      } else if (shouldKeepPosition && prevFirstMessage) {
        this.scrollKeepPosition(prevFirstMessage);
      }
    }
  }

  componentWillUnmount(): void {
    if (this.messagesList) {
      this.messagesList.removeEventListener('scroll', this.handleScroll);
    }

    clearInterval(this.intervalId);
  }

  initPolling(): void {
    this.intervalId = setInterval(() => {
      const {
        subcampaignId,
        isLoading,
        isLoadingMore,
        onLoadNewMessages
      } = this.props;

      if (!document.hidden && !isLoading && !isLoadingMore && !isNil(subcampaignId)) {
        onLoadNewMessages();
      }
    }, POLLING_INTERVAL);
  }

  scrollKeepPosition(oldFirstMessage: MessengerMessage): void {
    if (this.messagesList) {
      const firstMessageElement: HTMLElement | undefined | null = this.messagesList.querySelector<HTMLElement>(`[data-id="${oldFirstMessage.externalId}"]`);

      if (firstMessageElement && this.messagesList) { // flow will complain if this.messagesList is not here
        // keep current scroll position
        this.messagesList.scrollTop = firstMessageElement.offsetTop - this.messagesList.offsetTop;
      }
    }
  }

  scrollToBottom(): void {
    if (this.messagesList) {
      const scrollHeight = this.messagesList.scrollHeight;
      const height = this.messagesList.clientHeight;
      const maxScrollTop = scrollHeight - height;

      this.messagesList.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
    }
  }

  getLatestSystemMessageExternalIdForInfluencerAction(): LastestSystemMessageForInfluencerAction | undefined | null {
    const { isBrand, groupedMessages } = this.props;

    if (isBrand) {
      return null;
    }

    // $FlowIgnore
    const groups: Array<MessengerGroupedMessages> = groupedMessages ? Object.values(groupedMessages) : [];

    // to show action buttons only for the latest system message in the chat
    for (let i = groups.length - 1; i >= 0; i--) {
      for (let j = groups[i].messages.length - 1; j >= 0; j--) {
        const message = groups[i].messages[j];

        if (message.type === 'system'
          && ['influencer_invite', 'negotiation_reopen', 'influencer_quote'].includes(message.metadata.type.toLowerCase())
        ) {
          return {
            message,
            groupKey: groups[i].groupKey
          };
        }
      }
    }

    return null;
  }

  handleScroll = (): void => {
    const {
      isLoading,
      onLoadPreviousPage
    } = this.props;

    if (!isLoading && this.messagesList && this.messagesList.scrollTop === 0) {
      onLoadPreviousPage();
    }
  };

  render(): React.ReactNode {
    const {
      campaign,
      subcampaign,
      subcampaignId,
      isBrand,
      groupedMessages,
      isLoadingMore,
      isLoading
    } = this.props;

    const latestMessageExternalIdForInfluencerAction = this.getLatestSystemMessageExternalIdForInfluencerAction();

    return (
      <section
        className="messages"
        ref={(ref) => { this.messagesList = ref; }}
      >
        {
          (isLoading || isLoadingMore) &&
            <Spinner
              color="light"
              size="small"
              position="inline"
            />
        }
        {
          // $FlowIgnore
          !isLoading && groupedMessages && Object.values(groupedMessages).map(({ groupKey, messages }) => (
            <MessagesByDate
              key={groupKey}
              messages={messages}
              groupKey={groupKey}
              campaign={campaign}
              subcampaign={subcampaign}
              subcampaignId={subcampaignId}
              latestMessageExternalIdForInfluencerAction={
                latestMessageExternalIdForInfluencerAction && latestMessageExternalIdForInfluencerAction.groupKey === groupKey ?
                  latestMessageExternalIdForInfluencerAction.message.externalId :
                  null
              }
              isBrand={isBrand}
            />
          ))
        }
      </section>
    );
  }
}

const mapStateToProps = ({ messages }, props): StateProps => {
  const isLoading = messages.isLoadingBySubcampaign[props.subcampaignId];

  return {
    isLoadingMore: messages.isLoadingMore,
    isLoading: isNil(isLoading) ? true : isLoading,
    groupedMessages: messages.messagesBySubcampaign[props.subcampaignId]
  };
};

export default connect(mapStateToProps)(MessagesList);
